<template>
  <div>
    <div v-if="plan != null">
      <v-dialog
        v-model="showPlanEditDialog"
        min-width="600"
        max-width="1180"
        min-height="600"
        max-height="900"
        @keydown.esc="onClosePlanEditDialog" @click:outside="onClosePlanEditDialog"
      >
        <template>
          <plan-maintainer :plan="plan" @close-dialog="onClosePlanEditDialog"></plan-maintainer>
        </template>
      </v-dialog>

      <!-- Sin `persistent`: se cierra con Escape o clic afuera. El handler va también en
           `click:outside` porque cerrar por v-model no dispara el cierre del componente, y
           ahí es donde se limpia el servicio seleccionado y se recarga el plan. -->
      <v-dialog
        v-model="showServiceEditDialog"
        min-width="600"
        max-width="900"
        min-height="600"
        max-height="900"
        @keydown.esc="onCloseServiceEditDialog"
        @click:outside="onCloseServiceEditDialog"
      >
        <template>
          <service-plan-mantainer
            @close-dialog="onCloseServiceEditDialog"
            :service="selectedService"
            :plan="plan"
          ></service-plan-mantainer>
        </template>
      </v-dialog>

      <v-dialog
        v-model="showAddServiceDialog"
        min-width="600"
        max-width="900"
        min-height="600"
        max-height="900"
        @keydown.esc="onCloseAddServiceDialog" @click:outside="onCloseAddServiceDialog"
      >
        <template>
          <service-plan-mantainer
            @close-dialog="onCloseAddServiceDialog"
            :service="selectedService"
            :plan="plan"
          ></service-plan-mantainer>
        </template>
      </v-dialog>

      <v-dialog
        v-model="showAddBenefitDialog"
        min-width="600"
        max-width="900"
        min-height="600"
        max-height="900"
        @keydown.esc="onCloseAddBenefitDialog" @click:outside="onCloseAddBenefitDialog"
      >
        <template>
          <benefit-plan-mantainer
            @close-dialog="onCloseAddBenefitDialog"
            :plan="plan"
          ></benefit-plan-mantainer>
        </template>
      </v-dialog>

      <v-dialog
        v-model="showAddFeatureDialog"
        min-width="600"
        max-width="900"
        min-height="600"
        max-height="900"
        @keydown.esc="onCloseAddFeatureDialog" @click:outside="onCloseAddFeatureDialog"
      >
        <template>
          <feature-plan-mantainer
            @close-dialog="onCloseAddFeatureDialog"
            :plan="plan"
          ></feature-plan-mantainer>
        </template>
      </v-dialog>

      <v-dialog
        v-model="showAllianceExclusionDialog"
        min-width="600"
        max-width="900"
        @keydown.esc="onCloseAllianceExclusionDialog" @click:outside="onCloseAllianceExclusionDialog"
      >
        <template>
          <alliance-plan-mantainer
            @close-dialog="onCloseAllianceExclusionDialog"
            :plan="plan"
          ></alliance-plan-mantainer>
        </template>
      </v-dialog>

      <v-dialog
        v-model="deleteServiceDialog"
        min-width="600"
        max-width="900"
        min-height="600"
        max-height="900"
        @keydown.esc="closeDeleteServiceDialog" @click:outside="closeDeleteServiceDialog"
      >
        <template>
          <v-card class="pa-6">
            <v-card-title class="text-uppercase">
              <div>
                Eliminar servicio {{ selectedService != null ? selectedService.service.title : '' }}
              </div>
            </v-card-title>
            <hr class="my-3" />
            <v-card-text class="my-0 py-0">
              <p>¿Está seguro de que desea eliminar el servicio?</p>
            </v-card-text>
            <hr class="my-3" />
            <v-card-actions class="justify-end">
              <v-btn text color="primary" rounded @click="closeDeleteServiceDialog">Cancelar</v-btn>
              <v-btn
                color="primary"
                rounded
                @click="deleteService(selectedService.id)"
              >
                Sí, eliminar
              </v-btn>
            </v-card-actions>
          </v-card>
        </template>
      </v-dialog>

      <v-card class="pa-12">
        <v-row class="ma-0 pa-0">
          <div>
            <h1 class="text-h4 font-weight-bold">{{ plan.name }}</h1>
            <span
              v-if="plan.internalDescription != null && plan.internalDescription != ''"
              class="text-body-1"
              >{{ plan.internalDescription }}</span
            >
          </div>

        </v-row>

        <v-divider class="my-4"></v-divider>

        <h2 class="text-h6 mb-3">
          Servicios
          <v-btn
            v-if="can('plans:manage-services')"
            fab
            dark
            color="primary"
            @click="openAddServiceDialog"
            x-small
          >
            <v-icon dark>mdi-plus</v-icon>
          </v-btn>
        </h2>
        <v-card outlined>
          <v-data-table :headers="servicesTableHeaders" :items="sortedServices" hide-default-footer :items-per-page="-1">
            <template v-slot:item.service="{ item }">
              <div>{{ item.service.title }}</div>
            </template>

            <template v-slot:item.maxAnnualEvents="{ item }">
              <div v-if="item.maxAnnualEvents != null">
                {{ item.maxAnnualEvents }}
                {{ item.maxAnnualEvents != 1 ? 'eventos' : 'evento' }}
              </div>
              <div v-else>
                Ilimitado
              </div>
            </template>

            <template v-slot:item.maxAmountPerEvent="{ item }">
              <div v-if="item.maxAmountPerEvent != null">
                <span v-if="item.maxAmountPerEventCurrency == 'CLP' || item.maxAmountPerEventCurrency == '$'">{{
                  item.maxAmountPerEvent | clpCurrency({ fractionCount: 2 })
                }}</span>
                <span v-else-if="item.maxAmountPerEventCurrency == 'UF'"
                  >{{
                    item.maxAmountPerEvent == parseInt(item.maxAmountPerEvent)
                      ? item.maxAmountPerEvent
                      : item.maxAmountPerEvent.toFixed(2)
                  }}
                  {{ item.maxAmountPerEventCurrency }}</span
                >
                <span v-else>{{ item.maxAmountPerEvent }} {{ item.maxAmountPerEventCurrency }}</span>
              </div>
              <div v-else>
                --
              </div>
            </template>

            <template v-slot:item.maxAnnualAmount="{ item }">
              <div v-if="item.maxAnnualAmount != null">
                <span
                  v-if="
                    item.maxAnnualAmountCurrency == 'CLP' || item.maxAnnualAmountCurrency == '$'
                  "
                >
                  {{ item.maxAnnualAmount | clpCurrency({ fractionCount: 2 }) }}</span
                >
                <span v-else-if="item.maxAnnualAmountCurrency == 'UF'">
                  {{
                    item.maxAnnualAmount == parseInt(item.maxAnnualAmount)
                      ? item.maxAnnualAmount
                      : item.maxAnnualAmount.toFixed(2)
                  }}
                  {{ item.maxAnnualAmountCurrency }}</span
                >
                <span v-else>
                  {{ item.maxAnnualAmount }} {{ item.maxAnnualAmountCurrency }}</span
                >
              </div>
              <div v-else>
                --
              </div>
            </template>

            <template v-slot:item.serviceDeductible="{ item }">
              <div v-if="item.serviceDeductible">
                <!-- El deducible tiene su propia unidad: puede ser '%' aunque los límites estén en UF -->
                <span v-if="item.serviceDeductibleCurrency == '%'">{{ item.serviceDeductible }}%</span>
                <span v-else-if="item.serviceDeductibleCurrency == 'CLP' || item.serviceDeductibleCurrency == '$'">{{ item.serviceDeductible | clpCurrency({ fractionCount: 2 }) }}</span>
                <span v-else>{{ item.serviceDeductible }} {{ item.serviceDeductibleCurrency }}</span>
              </div>
              <div v-else>--</div>
            </template>

            <template v-slot:item.graceDays="{ item }">
              <div v-if="item.graceDays != null">
                {{ item.graceDays }}
                {{ item.graceDays != 1 ? 'días' : 'día' }}
              </div>
              <div v-else>
                --
              </div>
            </template>

            <template v-slot:item.contratoId="{ item }">
              <v-tooltip v-if="item.contratoId" top>
                <template v-slot:activator="{ on, attrs }">
                  <v-chip
                    small
                    dark
                    :color="contratoById[item.contratoId] ? 'teal' : 'orange darken-2'"
                    :href="contratoById[item.contratoId] ? contratoHref(contratoById[item.contratoId]) : undefined"
                    target="_blank"
                    rel="noopener"
                    v-bind="attrs"
                    v-on="on"
                  >
                    {{ contratoById[item.contratoId] ? contratoLabel(contratoById[item.contratoId]) : 'Contrato no encontrado' }}
                  </v-chip>
                </template>
                <span v-if="contratoById[item.contratoId]">Contrato TPA que factura este servicio. Clic para ver el contrato en una nueva pestaña</span>
                <span v-else>No se encontró el contrato {{ item.contratoId }}</span>
              </v-tooltip>
              <div v-else>--</div>
            </template>

            <template v-slot:item.refundRedirectProfileId="{ item }">
              <v-tooltip v-if="item.refundRedirectProfileId" top>
                <template v-slot:activator="{ on, attrs }">
                  <v-chip
                    small
                    dark
                    :color="refundRedirectById[item.refundRedirectProfileId] ? 'deep-purple' : 'orange darken-2'"
                    :to="{ name: 'RefundRedirectProfiles' }"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon x-small left>mdi-call-split</v-icon>
                    {{ refundRedirectLabel(item.refundRedirectProfileId) }}
                  </v-chip>
                </template>
                <span>
                  El miembro no pide este reembolso en Pawer: ve los contactos de
                  {{ refundRedirectLabel(item.refundRedirectProfileId) }}. Clic para ver los perfiles de redirección.
                </span>
              </v-tooltip>
              <div v-else class="grey--text">En Pawer</div>
            </template>

            <template v-slot:item.actions="{ item }">
              <div class="d-flex align-center">
                <v-tooltip v-if="can('plans:manage-services')" top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      class="mx-2"
                      fab
                      dark
                      depressed
                      x-small
                      v-bind="attrs"
                      v-on="on"
                      color="blue"
                      @click="openServiceEditDialog(item)"
                    >
                      <v-icon small dark>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>Editar</span>
                </v-tooltip>

                <v-tooltip v-if="can('plans:remove-services')" top>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      class="mx-2"
                      fab
                      dark
                      depressed
                      x-small
                      v-bind="attrs"
                      v-on="on"
                      color="pink"
                      @click="triggerDeleteServiceModal(item)"
                    >
                      <v-icon small dark>mdi-delete-forever</v-icon>
                    </v-btn>
                  </template>
                  <span>Eliminar</span>
                </v-tooltip>
              </div>
            </template>
          </v-data-table>
        </v-card>

        <v-divider class="my-4"></v-divider>

        <h2 class="text-h6 mb-3">
          Beneficios
          <v-btn
            v-if="can('plans:manage-benefits')"
            fab
            dark
            color="primary"
            @click="openAddBenefitDialog"
            x-small
          >
            <v-icon dark>mdi-plus</v-icon>
          </v-btn>
        </h2>

        <v-card outlined>
          <v-data-table :headers="benefitsTableHeaders" :items="sortedBenefits" hide-default-footer>
            <template v-slot:item.benefit="{ item }">
              <div>{{ item.value }}</div>
            </template>
          </v-data-table>
        </v-card>

        <v-divider class="my-4"></v-divider>

        <h2 class="text-h6 mb-3">
          Detalles
          <v-btn
            v-if="can('plans:manage-features')"
            fab
            dark
            color="primary"
            @click="openAddFeatureDialog"
            x-small
          >
            <v-icon dark>mdi-plus</v-icon>
          </v-btn>
        </h2>

        <v-card outlined>
          <v-data-table :headers="featuresTableHeaders" :items="features" hide-default-footer>
            <template v-slot:item.feature="{ item }">
              <v-text-field
                v-if="editingFeatureId === item.id"
                v-model="editingFeatureValue"
                dense
                hide-details
                autofocus
                @keydown.enter="saveFeatureEdit(item)"
                @keydown.esc="cancelEditFeature"
              ></v-text-field>
              <div v-else>{{ item.value }}</div>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn
                v-if="can('plans:manage-features')"
                class="mx-2"
                fab
                dark
                depressed
                x-small
                color="blue"
                @click="startEditFeature(item)"
              >
                <v-icon small dark>mdi-pencil</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>

        <v-divider class="my-4"></v-divider>

        <h2 class="text-h6 mb-3">
          Club de Descuentos
          <v-btn
            v-if="can('plans:manage-alliance-exclusions')"
            fab
            dark
            color="primary"
            @click="openAllianceExclusionDialog"
            x-small
          >
            <v-icon dark>mdi-cog</v-icon>
          </v-btn>
        </h2>

        <v-card outlined>
          <v-list dense v-if="excludedAlliances.length">
            <template v-for="(exc, i) in excludedAlliances">
              <v-list-item :key="exc.id">
                <v-list-item-avatar size="32">
                  <v-img v-if="exc.alliance && exc.alliance.imageCardUrl" :src="exc.alliance.imageCardUrl" />
                  <v-icon v-else small>mdi-tag</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="text-body-2">
                    {{ exc.alliance ? exc.alliance.name : exc.allianceId }}
                  </v-list-item-title>
                </v-list-item-content>
                <v-list-item-action>
                  <v-chip x-small color="red lighten-1" dark>Excluida</v-chip>
                </v-list-item-action>
              </v-list-item>
              <v-divider v-if="i < excludedAlliances.length - 1" :key="'d-' + i" />
            </template>
          </v-list>
          <v-card-text v-else class="grey--text text-body-2">
            No hay alianzas excluidas. Todas las alianzas públicas son visibles para los usuarios de este plan.
          </v-card-text>
        </v-card>
      </v-card>
    </div>
  </div>
</template>

<script>
import PlanMaintainer from '../components/PlanMaintainer';
import ServicePlanMantainer from '../components/ServicePlanMantainer';
import PlansService from '../services/plans.service';
import BenefitPlanMantainer from '../components/BenefitPlanMantainer';
import FeaturePlanMantainer from '../components/FeaturePlanMantainer';
import AlliancePlanMantainer from '../components/AlliancePlanMantainer';
import ContratoService from '../services/contrato.service';
import RefundRedirectProfilesService from '../services/refund-redirect-profiles.service';
import { can } from '../utils/permissions';

export default {
  components: {
    PlanMaintainer,
    ServicePlanMantainer,
    BenefitPlanMantainer,
    FeaturePlanMantainer,
    AlliancePlanMantainer
  },
  props: {
    planId: String
  },
  data() {
    return {
      plan: null,
      features: [],
      excludedAlliances: [],
      contratoOptions: [],
      refundRedirectProfiles: [],
      showPlanEditDialog: false,
      showServiceEditDialog: false,
      showAddServiceDialog: false,
      showAddBenefitDialog: false,
      showAddFeatureDialog: false,
      showAllianceExclusionDialog: false,
      deleteServiceDialog: false,
      deleteBenefitDialog: false,
      selectedService: null,
      editingFeatureId: null,
      editingFeatureValue: '',
      servicesTableHeaders: [
        { text: 'Orden', value: 'order', sortable: false },
        { text: 'Servicio', value: 'service', sortable: false },
        { text: 'Máx. eventos/año', value: 'maxAnnualEvents', sortable: false },
        { text: 'Límite por evento', value: 'maxAmountPerEvent', sortable: false },
        { text: 'Límite anual', value: 'maxAnnualAmount', sortable: false },
        { text: 'Deducible', value: 'serviceDeductible', sortable: false },
        { text: 'Carencia', value: 'graceDays', sortable: false },
        { text: 'Cubierto por', value: 'contratoId', sortable: false },
        { text: 'Reembolso', value: 'refundRedirectProfileId', sortable: false },
        {
          text: '',
          value: 'actions',
          sortable: false
        }
      ],
      benefitsTableHeaders: [
        { text: 'Orden', value: 'order', sortable: false },
        { text: 'Beneficio', value: 'benefit', sortable: false },
        { text: '', value: 'actions', sortable: false }
      ],
      featuresTableHeaders: [
        { text: 'Key', value: 'key', sortable: false },
        { text: 'Detalle', value: 'feature', sortable: false },
        { text: '', value: 'actions', sortable: false }
      ]
    };
  },
  computed: {
    sortedServices() {
      let services = [];
      if (this.plan && this.plan.servicePlanDetails) {
        services = this.plan.servicePlanDetails.slice();
        services.sort((a, b) => {
          return a.order - b.order;
        });
      }
      return services;
    },
    sortedBenefits() {
      let benefits = [];
      if (this.plan && this.plan.benefits) {
        benefits = this.plan.benefits.slice();
        benefits.sort((a, b) => {
          return a.order - b.order;
        });
      }
      return benefits;
    },
    // Contrato TPA que factura cada servicio, para mostrarlo en la tabla (y enlazarlo) en
    // vez del UUID crudo que trae `servicePlanDetails[].contratoId`.
    contratoById() {
      const map = {};
      this.contratoOptions.forEach(c => {
        map[c.id] = c;
      });
      return map;
    },
    // Perfil de redirección de cada servicio (spec 125), para mostrar la contraparte
    // en vez del UUID crudo de `servicePlanDetails[].refundRedirectProfileId`.
    refundRedirectById() {
      const map = {};
      this.refundRedirectProfiles.forEach(p => {
        map[p.id] = p;
      });
      return map;
    }
  },
  mounted() {
    if (this.planId) {
      this.getPlanDetails(this.planId);
      this.getFeaturesPlan(this.planId);
      this.getAllianceExclusions(this.planId);
      this.getContratoOptions();
      this.getRefundRedirectProfiles();
    }
  },

  methods: {
    can,
    async getPlanDetails(planId) {
      const res = await PlansService.getPlanDetails(planId);
      if (res.status === 'ok') {
        this.plan = res.data;
        console.log('plan:', this.plan);
      }
    },
    async deleteService(servicePlanId) {
      try {
        const res = await PlansService.removeServiceFromPlan(servicePlanId);
        if (res.status === 'ok') {
          this.getPlanDetails(this.planId);
          this.closeDeleteServiceDialog();
        }
      } catch (error) {
        console.log(error);
      }
    },
    async getFeaturesPlan(planId) {
      const res = await PlansService.getPlanFeatures(planId);
      if (res.status === 'ok') {
        this.features = res.data;
        console.log('features', this.features);
      }
    },
    async getAllianceExclusions(planId) {
      const res = await PlansService.getPlanAllianceExclusions(planId);
      if (res.status === 'ok') {
        this.excludedAlliances = res.data;
      }
    },
    /** "Empresa · código" del contrato; solo el código si no trae empresa. */
    contratoLabel(contrato) {
      const companyName = contrato.company && contrato.company.name;
      return companyName ? `${companyName} · ${contrato.codigo}` : contrato.codigo;
    },
    /** URL de la ficha del contrato, para abrirla en otra pestaña sin perder el plan. */
    contratoHref(contrato) {
      return this.$router.resolve({
        name: 'Contratos',
        query: { codigo: contrato.codigo, mode: 'view' }
      }).href;
    },
    /** Contraparte del perfil de redirección, o aviso si el perfil no se encontró. */
    refundRedirectLabel(profileId) {
      const profile = this.refundRedirectById[profileId];
      return profile ? profile.counterpartName : 'Perfil no encontrado';
    },
    async getRefundRedirectProfiles() {
      const res = await RefundRedirectProfilesService.getAll();
      if (res.status === 'ok') {
        this.refundRedirectProfiles = res.data || [];
      }
    },
    async getContratoOptions() {
      const res = await ContratoService.getAll();
      if (res.status === 'ok') {
        this.contratoOptions = res.data || [];
      }
    },
    openAllianceExclusionDialog() {
      this.showAllianceExclusionDialog = true;
    },
    onCloseAllianceExclusionDialog() {
      this.showAllianceExclusionDialog = false;
      this.getAllianceExclusions(this.planId);
    },
    triggerDeleteServiceModal(service) {
      this.deleteServiceDialog = true;
      this.selectedService = service;
    },
    closeDeleteServiceDialog() {
      this.deleteServiceDialog = false;
      this.selectedService = null;
      this.getPlanDetails(this.planId);
    },
    openPlanEditDialog() {
      this.showPlanEditDialog = true;
    },
    onClosePlanEditDialog() {
      this.showPlanEditDialog = false;
      this.getPlanDetails(this.planId);
    },
    openServiceEditDialog(service) {
      console.log(service);
      this.selectedService = service;
      this.showServiceEditDialog = true;
    },
    onCloseServiceEditDialog() {
      this.showServiceEditDialog = false;
      this.selectedService = null;
      this.getPlanDetails(this.planId);
    },
    openAddServiceDialog() {
      this.showAddServiceDialog = true;
    },
    onCloseAddServiceDialog() {
      this.showAddServiceDialog = false;
      this.getPlanDetails(this.planId);
    },
    openAddBenefitDialog() {
      this.showAddBenefitDialog = true;
      this.getPlanDetails(this.planId);
    },
    onCloseAddBenefitDialog() {
      this.showAddBenefitDialog = false;
      this.getPlanDetails(this.planId);
    },
    openAddFeatureDialog() {
      this.showAddFeatureDialog = true;
    },
    onCloseAddFeatureDialog() {
      this.showAddFeatureDialog = false;
      this.getPlanDetails(this.planId);
      this.getFeaturesPlan(this.planId);
    },
    startEditFeature(item) {
      this.editingFeatureId = item.id;
      this.editingFeatureValue = item.value;
    },
    async saveFeatureEdit(item) {
      const res = await PlansService.updateFeaturePlan(item.id, { key: item.key, value: this.editingFeatureValue });
      if (res.status === 'ok') {
        item.value = this.editingFeatureValue;
      }
      this.cancelEditFeature();
    },
    cancelEditFeature() {
      this.editingFeatureId = null;
      this.editingFeatureValue = '';
    }
  }
};
</script>
